import { Text, Button, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { useState, useEffect } from 'react';

interface LastFetchedProps {
  dataUpdatedAt: number;
  isFetching: boolean;
  onRefresh?: () => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  text: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  refreshButton: {
    minWidth: 'unset',
    padding: '2px 6px',
    color: tokens.colorNeutralForeground3,
  },
});

export function LastFetched({ dataUpdatedAt, isFetching, onRefresh }: LastFetchedProps) {
  const styles = useStyles();
  const [, setTick] = useState(0);

  // Auto-update the relative time display every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const getTimeAgo = () => {
    if (!dataUpdatedAt) return '';
    const seconds = Math.floor((Date.now() - dataUpdatedAt) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes === 1) return '1 minute ago';
    return `${minutes} minutes ago`;
  };

  if (isFetching) {
    return (
      <div className={styles.container}>
        <Spinner size="tiny" />
        <Text className={styles.text}>Refreshing...</Text>
      </div>
    );
  }

  if (!dataUpdatedAt) return null;

  return (
    <div className={styles.container}>
      <Text className={styles.text}>Updated {getTimeAgo()}</Text>
      {onRefresh && (
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowSyncRegular />}
          className={styles.refreshButton}
          onClick={onRefresh}
          title="Refresh data"
        />
      )}
    </div>
  );
}
